/**
 * One part read from a close-up photo: the markings of a protective device or an RCD (rating,
 * curve, poles, breaking capacity, sensitivity, type) or of a cable sheath (XVB 3G2,5). Used
 * from the check panel to fill in a value the diagram is missing (R-DEV-01, R-CIR-04).
 */
import { z } from 'zod';
import type { PropValue } from '@elec/domain';

export const partSchema = z.object({
  device: z
    .object({
      ratingA: z.number().positive().nullable(),
      curve: z.enum(['B', 'C', 'D']).nullable(),
      poles: z.number().int().min(1).max(4).nullable(),
      breakingKa: z
        .number()
        .positive()
        .nullable()
        .describe('Breaking capacity in kA (the boxed number, e.g. 6000 → 6)'),
      sensitivityMa: z.number().positive().nullable().describe('RCD: IΔn in mA'),
      rcdType: z.enum(['AC', 'A', 'F', 'B']).nullable(),
    })
    .nullable()
    .describe('The device shown, when the photo shows one'),
  cable: z
    .object({
      type: z.string().describe('Designation printed on the sheath: XVB, XGB, EXVB, VOB, YMvK, R2V…'),
      cores: z.number().int().min(1).max(7),
      sectionMm2: z.number().positive(),
    })
    .nullable()
    .describe('The cable marking shown, when the photo shows a cable'),
  notes: z.string().describe('What could not be read'),
});
export type Part = z.infer<typeof partSchema>;

export const PART_SYSTEM = `You read the markings of ONE electrical part on a close-up photo: a circuit breaker, an
RCD / RCBO, or a cable sheath. Report only what is printed and readable; anything else is null.
- "C16" = curve C, 16 A. The boxed number (3000, 4500, 6000, 10000) is the breaking capacity in A:
  return it in kA (6000 → 6).
- RCD: IΔn (30 mA, 300 mA) and the type symbol or letter (AC, A, F, B).
- Cable: the designation and "3G2,5" = 3 cores of 2.5 mm²; "3x1,5" = 3 cores of 1.5 mm².
- If the photo shows several devices, report the one in the centre and say so in notes.`;

/** The device props a reading fills in (only the values read). */
export function partDeviceProps(p: Part): Record<string, PropValue> {
  const d = p.device;
  if (!d) return {};
  const out: Record<string, PropValue> = {};
  for (const k of ['ratingA', 'curve', 'poles', 'breakingKa', 'sensitivityMa', 'rcdType'] as const)
    if (d[k] !== null) out[k] = d[k];
  return out;
}

export const DEMO_PART: Part = {
  device: { ratingA: 20, curve: 'C', poles: 2, breakingKa: 6, sensitivityMa: null, rcdType: null },
  cable: { type: 'XVB', cores: 3, sectionMm2: 2.5 },
  notes: '',
};
